import * as PopoverPrimitive from "@radix-ui/react-popover"
import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useAICoach } from './AICoachContext';
import { Alert, Button, ChatMessageView, Checkbox, Icon, Loading, Select, Skeleton, Tooltip, useToast, Popover, PopoverContent, PopoverTrigger, PopoverClose, Heading, Paragraph } from '@aidkitorg/component-library';
import { safeParse, useInterval, useMarkdown } from '../Util';
import { ChatMessageItemTypes } from '@aidkitorg/types/lib/convoai/conversation';
import { useLocalizedStrings, useLocalizedStringsNoContext } from '../Localization';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { useApplicant } from '../Contexts/ApplicantContext';
import { useContext } from 'react';
import InterfaceContext, { AuthContext, PublicConfigurationContext } from '../Context';
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { FeedbackButtons } from './FeedbackButtons';
import { usePost, useToken } from '../API';
import { ChatSessionContext } from '../Contexts/ChatSessionContext';
import { ChatConversationContext, useOpenAITextChat, useOpenAIVoiceChat } from '../Contexts/ChatContext';
import slugid from 'slugid';
import { v4 } from 'uuid';
import { hash } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { AudioInterfaceContext } from '../Contexts/AudioInterfaceContext';

// Default values in case config is not provided
export const DEFAULT_AGENT_NAME = "Opal";
export const DEFAULT_AVATAR_URL = "https://aidkit-public.s3.us-east-2.amazonaws.com/demo/Opal.jpg";

export function AICoachButton(props: { onClick: () => void }) {
  const L = useLocalizedStrings();
  const config = useContext(PublicConfigurationContext);
  const { markCTAAsSeen, hasShownCTA } = useContext(ChatSessionContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;
  
  function replaceAgentNameInString(s:string) {
    return s.replaceAll("$name", agentName);
  }
    
  return <Popover
    open={!hasShownCTA}
    onOpenChange={(open) => {
      if (!open) {
        markCTAAsSeen();
      }
    }}
  >    
    <PopoverContent 
      sideOffset={10}
      side="top"
      // this makes the popover "sticky" and can only be cleared by clicking the close button
      onInteractOutside={e => e.preventDefault()}
      onOpenAutoFocus={e => e.preventDefault()}
    >
      <div className="flex gap-2 relative max-w-64">
        <div className="flex-1">
          <Icon 
            className="text-indigo-600"
            type="solid"
            name="ChatBubbleOvalLeftEllipsisIcon" 
            size="xl" />          
        </div>
        
        <div className="flex flex-col flex-0 pr-6 gap-2 rtl:pl-6 rtl:pr-0">
          <Heading className="mb-0" level="h4">{replaceAgentNameInString(L.ai_coach.cta_heading)}</Heading>
          <Paragraph className="mt-0 mb-0" size="sm">{replaceAgentNameInString(L.ai_coach.cta_subheading)}</Paragraph>
        </div>
        
        <PopoverClose asChild>
          <Button 
            onClick={() => { 
              markCTAAsSeen();
            }}
            icon={<Icon name="XMarkIcon" />} 
            className="absolute p-2 -top-4 -right-4 rtl:-left-4 rtl:right-auto hover:opacity-60 focus:ring-0 focus-visible:ring-0"
            variant="unstyled" 
            size="md" />
        </PopoverClose>        
      </div>
      
    </PopoverContent>    
        
    <PopoverTrigger asChild>
      <button
        onClick={(e) => {
          props.onClick();
          e.preventDefault();
        }}
        className="pointer-events-auto bg-indigo-600 hover:bg-indigo-400 text-white text-lg font-semibold py-3 px-3.5 rounded-l-full rounded-r-full"
        aria-label={replaceAgentNameInString(L.ai_coach.ask_your_ai_coach)}
      >
        <span className="text-xs flex items-center gap-1">
          <Icon name="ChatBubbleLeftRightIcon" role="decorative" className="text-white" size="sm" strokeWidth={1.5} />
          <span className="hidden lg:block">{replaceAgentNameInString(L.ai_coach.ask_name)}</span>
        </span>
      </button>      
    </PopoverTrigger>
        
  </Popover>
}

const AudioInputDeviceKey = "audio_input_device";
function AudioInputSelector(props: {
  onSelectDevice: (result: { device: MediaStream | null }) => void,
  onError: (err: any) => void
}) {
  const L = useLocalizedStrings();
  const savedDeviceInfo = safeParse(sessionStorage.getItem(AudioInputDeviceKey)!, null) as MediaDeviceInfo;
  const [availableDeviceInfos, setAvailableDeviceInfos] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<MediaDeviceInfo | null>(savedDeviceInfo ?? null);
  const [remember, setRemember] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<{ value: string, label: string } | null>(null);
  const [deviceError, setDeviceError] = useState<any>(null);
  const options = useMemo(
    () => availableDeviceInfos.map(d => ({ value: d.deviceId, label: d.label || d.kind })),
    [availableDeviceInfos]
  );

  useAsyncEffect(async () => {
    let availableDevices = [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      availableDevices = devices.filter(d => d.kind === "audioinput");
      setAvailableDeviceInfos(availableDevices);
    } catch (e) {
      props.onError("An error occurred while accessing input devices");
      console.error(e);
    }
    if (!availableDevices.length) {
      props.onError("Failed to access audio input devices");
    }
  }, []);

  useAsyncEffect(async () => {
    if (selectedDeviceInfo) {
      let constraints: MediaStreamConstraints = {};
      if (selectedDeviceInfo.deviceId) {
        constraints = { audio: { deviceId: { exact: selectedDeviceInfo.deviceId } } };
      } else {
        constraints = { audio: true };
      }
      try {
        setDeviceError(null);
        const device = await navigator.mediaDevices.getUserMedia(constraints);
        if (!device) {
          throw new Error(`Could not find device for device id ${selectedDeviceInfo.deviceId}`);
        }
        // success callback
        props.onSelectDevice({ device });

        // save to local storage
        if (remember && typeof selectedDeviceInfo.toJSON === "function") {
          sessionStorage.setItem(AudioInputDeviceKey, JSON.stringify(selectedDeviceInfo.toJSON()));
        }
      }
      catch (err) {
        console.error(err);
        setDeviceError(err);
        setSelectedDeviceInfo(null);
        props.onError("Failed to get device");
      }
    }
  }, [remember, selectedDeviceInfo]);

  useEffect(() => {
    if (options.length && !selectedOption) {
      setSelectedOption(options[0]);
    }
  }, [selectedOption, options]);

  if (savedDeviceInfo && selectedDeviceInfo && !deviceError) {
    return null;
  }

  if (!options.length || selectedDeviceInfo) {
    return <Loading />
  }

  return <div className="border-t bg-gray-50 border-gray-200 p-3">
    <form className="flex flex-col gap-2" onSubmit={e => {
      e.preventDefault();
      const selectedDeviceInfo = availableDeviceInfos.find(d => d.deviceId === selectedOption?.value) ?? null;
      if (selectedDeviceInfo) {
        setSelectedDeviceInfo(selectedDeviceInfo);
      }
    }}>
      <div>
        <Select
          label={L.ai_coach.microphone}
          labelLocation="above"
          anchor="top"
          description={L.ai_coach.select_microphone}
          value={selectedOption || options[0]}
          onChange={option => setSelectedOption(option as typeof selectedOption)}
          options={options} />
      </div>
      <div className="flex gap-2 items-center">
        <Checkbox
          label={L.ai_coach.remember}
          checked={remember}
          onChange={v => setRemember(v as boolean)}
        />
        <Button
          className="grow"
          type="submit"
          icon={<Icon name="CheckCircleIcon" type="solid" size="xl" />}
          isDisabled={!options.length}>
        </Button>
      </div>
    </form>
  </div>
}

export const MessageContent = ({ message, showFeedbackButtons }: { message: ChatMessageItemTypes['message'], showFeedbackButtons?: boolean }) => {
  const contentDOMRef = useRef<HTMLDivElement | null>(null);
  const authContext = useContext(AuthContext);
  const [, token] = useToken();
  const getSignedUrl = usePost('/v2/document/view');
  const [contents, setContents] = useState<string[]>([]);

  useAsyncEffect(async () => {
    let contentToUse: typeof contents = [];
    for (const part of message.content) {
      switch (part.type) {
        case 'text':
          const parsed = safeParse(part.content, null);
          if(parsed) {
            contentToUse.push(`<pre>${JSON.stringify(parsed, null, 2)}</pre>`);
          } else {
            contentToUse.push(part.content);
          }
          break;
        case 'image':
          try {
            const url = new URL(part.url);
            url.search = '';
            const { paths } = await getSignedUrl({
              paths: [url.toString()]
            });
            paths[url.toString()]?.forEach((signedUrl: string) => {
              contentToUse.push(`<img src="${signedUrl}" height="400" width="400" />`);
            });
          } catch {
            contentToUse.push('Could not load image');
          }
          break;
        default:
          break;
      }
    }
    setContents(contentToUse);
  }, [message]);

  const content = useMarkdown(contents.join('\n'));

  // for any links to any subsurveys, add auth token as ?key url parameter
  useEffect(() => {
    if (contentDOMRef.current) {
      const contentLinks = contentDOMRef.current.querySelectorAll("a[href]");
      for (const link of Array.from(contentLinks)) {
        const relativePath = link.getAttribute("href")!.replace(new RegExp(`^${window.location.origin}`), "");
        if (relativePath.startsWith("/p/")) {
          const url = new URL(relativePath, window.location.origin);
          url.searchParams.set("key", authContext?.token?.() ?? token as string);
          link.setAttribute("href", url.href);
        }
      }
    }
  }, [content, contentDOMRef, authContext?.token?.(), token]);

  if (message.meta?.phase === "in_process") {
    return <Icon name="ChatBubbleOvalLeftEllipsisIcon" />;
  }
  if (message.meta?.phase === "pending") {
    return <Icon name="EllipsisHorizontalIcon" />;
  }
  return <div className="coach-chat-content overflow-x-hidden flex flex-col gap-2 divide-y divide-solid divide-indigo-200">
    <div ref={contentDOMRef}>{content}</div>
    {(message.role === "assistant" && showFeedbackButtons) && <div className="font-normal px-3 pt-2 -mx-4">
      <FeedbackButtons createdAt={message.meta?.time} id={message.id!} {...message.meta?.feedback ?? {}} />
    </div>}
  </div>
};

function AudioModalityView({ onStop }: { onStop: () => void }) {
  const L = useLocalizedStrings();
  const [chatAudioStart, setChatAudioStart] = useState<number | null>(null);
  const [chatAudioDuration, setChatAudioDuration] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { inputAudio, setInputAudio, setOutputAudio } = useContext(AudioInterfaceContext);
  const { toast } = useToast();

  const { phase } = useContext(ChatConversationContext);

  useOpenAIVoiceChat();

  useEffect(() => {
    if (audioRef.current) {
      setOutputAudio(audioRef.current)
    }
    return () => {
      setOutputAudio(null);
    }
  }, [audioRef]);

  useInterval(() => setChatAudioDuration(Math.floor((Date.now() - (chatAudioStart ?? Date.now())) / 1000)), 1)

  const chatAudioDurationDisplay = useMemo(() => {
    const seconds = chatAudioDuration;

    if (!Number.isFinite(seconds) || seconds < 0) {
      return '00:00:00';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const pad = (num: number): string => num.toString().padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }, [chatAudioDuration]);

  const close = () => {
    inputAudio?.getAudioTracks().forEach(t => t.stop());
    onStop();
  };

  useEffect(() => {
    setChatAudioStart(inputAudio ? Date.now() : null);
  }, [inputAudio]);

  // on unmount, stop microphone
  useEffect(() => () => close(), []);

  return <>
    {!inputAudio?.active && <AudioInputSelector
      onSelectDevice={
        ({ device }) => {
          if (device) {
            setInputAudio(device);
          }
        }
      }
      onError={
        (err) => {
          toast({
            variant: "error",
            description: err.message ?? err
          });
          onStop();
        }
      } />
    }
    <div className="flex w-full">
      <div className="w-[80%] flex space-x-4">
        <Button
          variant={"danger"}
          shape="circle"
          aria-label={L.ai_coach.voice_input}
          onClick={() => close()}
        >
          <Icon name="XMarkIcon" />
        </Button>
        <div className="content-center">{chatAudioDurationDisplay}</div>
      </div>
      <div className="content-center end-1 w-[20%]">
        {L.ai_coach.phases[(!phase || phase === 'unknown') ? 'listening' : phase]}
      </div>
      <audio ref={audioRef} hidden autoPlay={true} />
    </div>
  </>
}

function TextModalityView({ onVoiceEnable }: { onVoiceEnable: () => void }) {
  const L = useLocalizedStrings();
  const { config } = useAICoach();
  const [inputText, setInputText] = useState<string>("");
  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set());
  const chatForm = useRef<HTMLFormElement | null>(null);

  const { phase } = useContext(ChatConversationContext);
  const { setEnabled, sendMessage, enabled, ready, active } = useOpenAITextChat();

  useEffect(() => {
    setEnabled(true);
  }, []);

  const handleChatSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    const trimmedInput = inputText.trim();
    if (trimmedInput) {
      sendMessage({
        id: slugid.encode(v4()),
        role: 'user',
        type: 'message',
        content: [{ type: 'text', content: trimmedInput }]
      }, { createResponse: true });
      setInputText('');
    }
  }

  return <form
    ref={chatForm}
    className="flex items-center gap-2"
    onSubmit={handleChatSubmit}>
    {
      /* Microphone button */
      config?.enableVoice && <Button
        className="shrink-0"
        variant={"secondary"}
        shape="circle"
        aria-label={L.ai_coach.voice_input}
        onClick={() => onVoiceEnable()}
      >
        <Icon name="MicrophoneIcon" />
      </Button>
    }
    <textarea
      className="flex-1 shrink-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      rows={1}
      placeholder={`${L.ai_coach.type_a_message}...`}
      value={inputText}
      onKeyDown={e => setActiveKeys(activeKeys => activeKeys.add(e.key))}
      onKeyUp={e => {
        setActiveKeys(activeKeys => {
          activeKeys.delete(e.key);
          return activeKeys;
        });

        // allow enter if shift is being pressed, otherwise have it submit the form
        if (e.key === "Enter" && !activeKeys.has('Shift') && chatForm.current && inputText) {
          chatForm.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
          );
        }
      }}
      onChange={(e) => setInputText(e.target.value)}
    />
    <Button
      type="submit"
      className="shrink-0 px-2"
      isDisabled={!inputText?.trim() || !active || !ready || !enabled || phase !== 'listening'}
    >
      <Icon
        className="-rotate-45 origin-[50%_40%]"
        name="PaperAirplaneIcon" />
    </Button>
  </form>;
}

function QuickStartButtons() {
  const { config } = useAICoach();
  const chat = useContext(ChatConversationContext);
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);

  if (!config?.quickStart?.items?.length) {
    return null;
  }

  return <div>
    <div className="text-sm mb-2.5 -mt-2">{L.ai_coach.quickstart_topics_preamble}</div>
    <div className="flex flex-wrap gap-2">
      {config.quickStart.items.map((item, i) => {
        const content = item[context.lang] ?? item["en"]
        return <Button
          key={i}
          className="rounded-full"
          style={{ borderRadius: '9999px' }}
          onClick={() => chat.dispatch({
            id: slugid.encode(v4()),
            type: 'message',
            role: 'user',
            content: [{ type: 'text', content }],
            meta: {
              time: Date.now(),
              createResponse: true
            }
          })}
          variant="secondary">{content}</Button>
      })}
    </div>
  </div>;
}

export const AI_COACH_TERMS_ACCEPTED_KEY = "_ai_coach_terms_accepted_";
function ThinkingIndicator({ text = "Loading, please wait" }) {
  return <div
    className="flex justify-center items-center"
    role="status"
    aria-live="polite"
    aria-label={text}
  >
    <span className="sr-only">{text}</span>
    <div className="flex gap-1">
      {new Array(3).fill(null).map(
        (_, i) => <div key={i} className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" aria-hidden="true" style={{ animationDelay: `${0.3 * i}s` }}></div>
      )}
    </div>
  </div>;
}

export function AICoachChatWindow(props: { onClickMinimize: () => void }) {
  const L = useLocalizedStrings();
  const config = useContext(PublicConfigurationContext);  
  const { markCTAAsSeen, hasShownCTA } = useContext(ChatSessionContext)
  const { lang } = useContext(InterfaceContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;
  const avatarUrl = config?.capabilities?.aiCoach?.appearance?.avatarUrl || DEFAULT_AVATAR_URL;
  const { dispatch: chatDispatch } = useContext(ChatConversationContext);
  const chatSession = useContext(ChatSessionContext);
  const { messages, errors, phase } = useContext(ChatConversationContext);
  const [modality, setModality] = useState<'text' | 'audio'>('text');

  const termsCopyEnglish = useLocalizedStringsNoContext("en").ai_coach.terms_full;
  const latestTermsHash = useMemo(() => hash(termsCopyEnglish), [termsCopyEnglish]);

  const { config: aiCoachConfig, enabled } = useAICoach();

  const { info, update: setInfo } = useApplicant();
  const AICoachTermsAcceptedKey = "ai_coach_terms_accepted_hash";
  const hasAcceptedTerms = info[AICoachTermsAcceptedKey] === latestTermsHash;

  function clearConvo() {
    chatSession.clearSession();
    chatDispatch({ type: 'clear_chat' });
    setModality('text');
  };
  
  useLayoutEffect(() => { 
    if (!hasShownCTA) {
      markCTAAsSeen();
    }    
  }, [hasShownCTA, markCTAAsSeen])
  
  const messageBubbles: Parameters<typeof ChatMessageView>[0]['transcript'] = useMemo(() => {
    if (!hasAcceptedTerms) {
      return [];
    }
    let items: Parameters<typeof ChatMessageView>[0]['transcript'] = [];

    const quickStartContent = aiCoachConfig?.quickStart?.intro[lang] ?? aiCoachConfig?.quickStart?.intro["en"];
    if (quickStartContent) {
      const meta = {
        time: new Date(chatSession.createdAt ?? Date.now()).getTime()
      }
      items.push({
        role: "assistant",
        content: <MessageContent message={{
          id: '__quick_start_options__',
          type: 'message',
          role: 'assistant',
          content: [{
            type: "text",
            content: quickStartContent
          }],
          meta
        }} />,
        afterContent: <QuickStartButtons />,
        meta
      });
    }

    const unique = messages.reduce((items, msg) => {

      if (msg.type !== 'message') return items;
      if (items.length === 0) return [msg];

      const prev = items[items.length - 1];


      if (prev.role === msg.role) {
        items[items.length - 1] = {
          ...prev,
          content: prev.content.concat({ type: 'text', content: '\n' }, msg.content)
        };
        return items;
      } else {
        return items.concat(msg);
      }
    }, [] as ChatMessageItemTypes['message'][]);

    for (const t of unique) {
      items.push({
        role: t.role,
        meta: { time: Date.now(), ...(t.meta ?? {}) },
        content: <MessageContent message={t} showFeedbackButtons={t.role === "assistant"} />
      });
    }

    if (phase === "thinking") {
      items.push({
        role: "assistant",
        meta: { time: 0 },
        skipTimestamp: true,
        content: <ThinkingIndicator text={L.ai_coach.coach_is_processing.replace("$name", agentName)} />
      })
    }

    return items;
  }, [hasAcceptedTerms, messages]);

  const termsContent = useModularMarkdown({
    content: L.ai_coach.terms_full,
    info: {
      name: agentName,
      termsofservice: '[Terms of Service](https://www.rocketlawyer.com/sem/artificial-intelligence-terms-of-service)',
      privacypolicy: '[Privacy Policy](/privacy)'
    }
  });

  /* Sliding chat panel - full screen on mobile, partial on desktop */
  return <div className="fixed bottom-0 ltr:right-0 rtl:left-0 z-50 w-full sm:w-80 md:w-[360px] lg:w-[450px] max-w-[100vw] sm:max-w-[90vw] 
                      h-full sm:h-[90vh] md:h-[75vh] bg-white shadow-lg flex flex-col sm:rounded-t-lg">
    {/* Header */}
    <div className="border-b border-gray-200">
      <div className="flex flex-col p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={avatarUrl} alt={agentName} className="w-12 h-12 rounded-full" />
            <h3 className="text-2xl font-semibold mt-2">{L.ai_coach.chat_with.replace('$name', agentName)}</h3>
          </div>
          {/* Minimize button */}
          <div className="group relative space-x-1">
            <Button onClick={() => clearConvo()}>
              <Icon name="TrashIcon" strokeWidth={2} />
            </Button>
            <Button
              className="p-2"
              variant="secondary"
              onClick={props.onClickMinimize}
              aria-label={L.ai_coach.minimize_chat}
            >
              <Icon name="MinusIcon" className="text-red-600" strokeWidth={2} />
              <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {L.ai_coach.minimize_chat}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Loading skeleton */}
    {(enabled && !messageBubbles.length) && <div className="flex-0 overflow-hidden"><Skeleton className="p-4" variant="sentences" size="sm" /></div>}

    {hasAcceptedTerms && <ChatMessageView
      transcript={messageBubbles}
      roles={{
        assistant: {
          side: 'left',
          color: 'bg-indigo-100',
          icon: <img src={avatarUrl} alt={agentName} className="rounded-full" />
        },
        user: {
          side: 'right',
          icon: <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-semibold text-white">{info.legal_name?.substring(0, 1)?.toUpperCase() || 'U'}</div>,
          color: 'bg-gray-100',
        }
      }}
      autoScroll={true}
    />}

    {chatSession.isLoading && <Loading />}

    {/* Terms of Service - shown only before acceptance */}
    {(!hasAcceptedTerms && chatSession.loaded) && (
      <div className="flex flex-col p-4 border-t border-gray-200 bg-gray-50 overflow-auto flex-1">
        <h4 className="font-semibold text-gray-800 mb-2">{L.terms_and_conditions}</h4>
        <div className="flex-1">{termsContent}</div>
        <Button
          onClick={() => setInfo({ ...info, [AICoachTermsAcceptedKey]: latestTermsHash })}
          className="w-full mt-2">
          {L.ai_coach.accept_terms}
        </Button>
      </div>
    )}

    {/* Error */}
    {!!errors?.length && <div className="px-2 pb-2" hidden={true}>
      <Alert
        className="flex flex-col gap-2 p-3"
        dismissible={true}
        variant="danger">
        <div>{L.ai_coach.coach_error_message}</div>
        <Button
          size="sm"
          icon={<Icon name="ArrowPathIcon" />}
          onClick={() => location.reload()}>
          {L.applicant.history.refresh}
        </Button>
      </Alert>
    </div>}

    {/* Message input - only shown after terms acceptance */}
    {hasAcceptedTerms && <div className="border-t border-gray-200 p-2 md:p-3">
      {
        (modality === 'audio')
          ? <AudioModalityView onStop={() => setModality('text')} />
          : <TextModalityView onVoiceEnable={() => setModality('audio')} />
      }
    </div>}
  </div>
}
